import React from 'react';
import {
  ErrorComponentProps,
  Outlet,
  createFileRoute,
  redirect,
} from '@tanstack/react-router';
import { InvalidAbilityError } from '@area-restrita/shared/authorization';

import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';
import { Icons } from '@/components/icons';
import NotFound from '@/templates/NotFound';
import AppSidebar from '@/components/layout/AppSidebar';
import { Button } from '@/components/ui/button';
import { useSessionSidebarItems } from '@/constants/navigation/sidebar';

export const Route = createFileRoute('/_authenticated/_system')({
  head: () => ({
    meta: [{ title: 'Sistema' }],
  }),
  component: function SystemRouter() {
    const links = useSessionSidebarItems();

    return (
      <AppSidebar navItems={links}>
        <Outlet />
      </AppSidebar>
    );
  },
  beforeLoad: () => {
    const { session } = useAuthSessionStore.getState();

    if (!session?.organization) {
      throw redirect({ to: '/onboarding' });
    }
  },
  pendingComponent: function SystemPendingRouter() {
    const links = useSessionSidebarItems();

    return (
      <AppSidebar navItems={links}>
        <div className="flex-1 flex justify-center items-center">
          <Icons.spinner className="h-8 w-8 animate-spin" />
        </div>
      </AppSidebar>
    );
  },
  notFoundComponent: NotFound,
  errorComponent: (props: ErrorComponentProps) => {
    if (props.error instanceof InvalidAbilityError) {
      return <AbilityErrorComponent {...props} />;
    }

    return <BaseErrorComponent {...props} />;
  },
});

const BaseErrorComponent: React.FC<ErrorComponentProps> = ({
  error,
  info,
  reset,
}) => {
  const links = useSessionSidebarItems();

  return (
    <AppSidebar navItems={links}>
      <div className="flex-1 flex justify-center items-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Erro</h1>
          <p className="mb-4">{error.message}</p>
          {info && (
            <pre className="text-sm text-gray-500">{info.componentStack}</pre>
          )}
          <Button onClick={reset}>Tentar novamente</Button>
        </div>
      </div>
    </AppSidebar>
  );
};

const AbilityErrorComponent: React.FC<ErrorComponentProps> = () => {
  const { signOut } = useAuthSessionStore();

  return (
    <AppSidebar navItems={[]}>
      <div className="flex-1 flex justify-center items-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Acesso Negado</h1>
          <p className="mb-4">
            Houve um erro ao verificar suas permissões.
            <br />
            Por favor, tente novamente mais tarde ou entre em contato com o
            suporte.
          </p>

          <Button onClick={signOut}>Sair para a página inicial</Button>
        </div>
      </div>
    </AppSidebar>
  );
};
